import React, { useState, useCallback } from 'react';
import api from '../API/ApiService';
import toast from 'react-hot-toast';
import LoaderOverlay from '../Loader/LoaderOverlay';
import logo from '../../assets/logo.png';
import {appKeys} from '../../config';
import KycLandingPage from '../DemoForm/KycLandingPage';

const PublicNavbar = () => {
  return (
    <nav className="bg-white shadow-sm border-b border-gray-200 fixed top-0 left-0 right-0 z-10">
      <div className="px-6 py-4">
        <div className="flex items-center justify-center">
          <img src={logo} alt="Logo" className="h-8 w-auto" />
          <h1 className="ml-3 text-xl font-bold text-gray-600">Demo Portal</h1>
        </div>
      </div>
    </nav>
  );
};

const PublicSignerForm = () => {
    const [loading, setLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [docUrl, setDocUrl] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        idNumber: ''
    });

    const handleInputChange = useCallback((field) => (e) => {
        setFormData(prev => ({
            ...prev,
            [field]: e.target.value
        }));
    }, []);

    const handleProceed = (url) => {
        window.location.href = url;
    };

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const fetchSdkToken = await api.validateAppKeys(appKeys);
            if (!fetchSdkToken.status) {
                setLoading(false);
                toast.error('Something went wrong');
                return;
            }
            const response = await api.createESignRequests(
                formData?.name, 
                formData?.email, 
                formData?.idNumber, 
            );

            if (response.status) {
                localStorage.setItem('requestInit', 'public-request');
                toast.success('Initiating mock KYC request');
                setTimeout(()=> {
                    window.location.href = response?.data?.participants[0]?.signUrl
                }, 1000);
                setDocUrl(response?.data?.participants[0]?.signUrl);
                setShowSuccess(false); // set this to true to show landing page, remove location.href
                setLoading(false);
            }
        } catch (error) {
            console.error('Error creating request:', error);
            toast.error('Failed to submit request. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [formData]);

    if (loading) return <LoaderOverlay />;

    if (showSuccess) {
        return (
            <>
                <PublicNavbar />
                <div className="min-h-screen pt-24 bg-gray-50">
                    <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                            <KycLandingPage onProceed={handleProceed} docUrl={docUrl} />
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <PublicNavbar />
            <div className="min-h-screen pt-24 bg-gray-50">
                <div className="sm:mx-auto sm:w-full sm:max-w-7xl mb-6 px-4 sm:px-6 lg:px-8">
                    <h1 className="text-2xl font-bold text-center text-gray-900 mb-3">
                        Welcome to The world’s first tech that merges AI and biometrics in digital transactions.
                    </h1>
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 shadow-sm mx-2">
                        <div className="flex flex-col gap-2">
                            <h2 className="text-lg font-semibold text-blue-800">
                                Introducing BioConsent
                            </h2>
                            <div className="text-gray-700 text-sm">
                                <p> Recreating a face-to-face transaction in the digital ecosystem, our BioConsent engine uses live, real-time facial recognition and biometrics to tell exactly WHO did WHAT, WHERE, WHEN, and HOW — while defending against fraudsters, 
                                AI, bots, and synthetic identity theft.</p>
                                
                                {/* <div className="mt-1">
                                    <p><b>How it works: </b>
                                    During account sign-ups, access, or any sensitive action vulnerable to identity fraud or AI threats, faceEsign BioConsent runs a fast biometric check — verifying the user is real (not AI), present, and authorized.
                                    It takes seconds and fits seamlessly into your existing flow — no disruption, no replacements, no complicated integrations.</p>
                                </div> */}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                        <div className="bg-blue-600 px-6 py-8 text-center">
                            <h2 className="text-3xl font-extrabold text-white uppercase">
                                try now
                            </h2>
                            <p className="mt-2 text-blue-100">
                            Just enter a name and email below to start the 30 second  demo (feel free to use a fake name and email (example: John Wick and dog@wick.com).

                            </p>
                        </div>

                        <div className="px-6 py-8">
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700">
                                        Full Name
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.name}
                                        onChange={handleInputChange('name')}
                                        className="mt-1 block w-full px-4 py-3 rounded-md border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Enter your full name"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700">
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        required
                                        value={formData.email}
                                        onChange={handleInputChange('email')}
                                        className="mt-1 block w-full px-4 py-3 rounded-md border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Enter your email"
                                    />
                                </div>

                                {/* <div>
                                    <label className="block text-sm font-semibold text-gray-700">
                                        ID Number
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.idNumber}
                                        onChange={handleInputChange('idNumber')}
                                        className="mt-1 block w-full px-4 py-3 rounded-md border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Enter your ID number"
                                    />
                                </div> */}

                                <button
                                    type="submit"
                                    className="w-full flex justify-center py-3 px-4 rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 uppercase"
                                >
                                    next
                                </button>
                            </form>
                        </div>

                        <div className="px-6 py-4 bg-gray-50 text-center text-sm text-gray-600">
                            Enter made-up name and email then click NEXT (email needs @ and dot com)
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PublicSignerForm;